import { useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router";
import { api } from "@/lib/api";
import { adminKeys } from "@/lib/query";
import { brandColors as colors } from "@/lib/palette";
import { fonts } from "@/lib/fonts";
import { dateSortValue, formatDate, formatMoney } from "@/lib/format";
import {
  AdminTableControls,
  AdminTableFilterSelect,
  useAdminTable,
} from "@/components/admin/AdminTableControls";
import { AdminDataTable, type AdminDataTableColumn } from "@/components/admin/AdminDataTable";

type Registration = {
  id: string;
  retreatId: string | null;
  retreatTitle: string | null;
  name: string;
  email: string;
  tierName: string | null;
  amountPaidCents: number;
  balanceDueCents: number;
  addOns: { name: string; priceCents: number }[];
  status: "pending" | "confirmed" | "balance-due" | "fully-paid" | "cancelled";
  createdAt: string;
};

type ImportResult = {
  imported: number;
  skipped: number;
  errors: string[];
};

type RegistrationSortKey = "date" | "retreat" | "email" | "tier" | "paid" | "balance" | "status";

const IMPORT_COLUMNS =
  "retreat_slug, name, email, tier_name, tier_type, amount_paid, balance_due, addon_name, addon_price, status, stripe_payment_id, created_at";

function registrationSearchText(r: Registration) {
  return [
    r.name,
    r.email,
    r.retreatTitle,
    r.tierName,
    r.status,
    formatMoney(r.amountPaidCents),
    formatMoney(r.balanceDueCents),
    formatDate(r.createdAt),
  ]
    .filter(Boolean)
    .join(" ");
}

const registrationSorters: Record<RegistrationSortKey, (row: Registration) => string | number> = {
  date: (row) => dateSortValue(row.createdAt),
  retreat: (row) => row.retreatTitle ?? "",
  email: (row) => row.email,
  tier: (row) => row.tierName ?? "",
  paid: (row) => row.amountPaidCents,
  balance: (row) => row.balanceDueCents,
  status: (row) => row.status,
};

export default function AdminRegistrations() {
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const retreatId = searchParams.get("retreatId");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [balanceFilter, setBalanceFilter] = useState("all");

  // import panel state
  const [csvText, setCsvText] = useState("");
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [importErr, setImportErr] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  // balance-due payment links — per-row busy state + result/error message
  const [busyId, setBusyId] = useState<string | null>(null);
  const [rowMsg, setRowMsg] = useState<Record<string, { text: string; ok: boolean }>>({});

  const registrationsQuery = useQuery({
    queryKey: adminKeys.registrations(retreatId),
    queryFn: () =>
      api.get<{ registrations: Registration[] }>(
        retreatId
          ? `/api/admin/registrations?retreatId=${encodeURIComponent(retreatId)}`
          : "/api/admin/registrations",
      ),
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
  });
  const list = registrationsQuery.data?.registrations ?? [];

  async function sendBalanceLink(reg: Registration, notify: boolean) {
    setBusyId(reg.id);
    setRowMsg((m) => ({ ...m, [reg.id]: { text: notify ? "Sending…" : "Generating…", ok: true } }));
    try {
      const res = await api.post<{ url: string; expiresAt: string; emailed: boolean }>(
        "/api/retreats/balance-link",
        { registrationId: reg.id, notify },
      );
      let text: string;
      if (notify) {
        text = res.emailed ? `Emailed to ${reg.email}` : "Link ready (email not configured)";
      } else {
        text = "Copied to clipboard";
        try {
          await navigator.clipboard.writeText(res.url);
        } catch {
          text = res.url; // clipboard blocked — surface the URL to copy manually
        }
      }
      setRowMsg((m) => ({ ...m, [reg.id]: { text, ok: true } }));
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: adminKeys.registrations(retreatId) }),
        queryClient.invalidateQueries({ queryKey: adminKeys.sales() }),
        queryClient.invalidateQueries({ queryKey: adminKeys.retreats() }),
      ]);
    } catch (e) {
      setRowMsg((m) => ({
        ...m,
        [reg.id]: { text: e instanceof Error ? e.message : "Failed", ok: false },
      }));
    } finally {
      setBusyId(null);
    }
  }

  // comp VIP on/off — free upgrade recorded at $0, no Stripe involved
  async function toggleVip(reg: Registration, vip: boolean) {
    setBusyId(reg.id);
    setRowMsg((m) => ({ ...m, [reg.id]: { text: vip ? "Adding VIP…" : "Removing…", ok: true } }));
    try {
      await api.post(`/api/admin/registrations/${reg.id}/vip`, { vip });
      setRowMsg((m) => ({ ...m, [reg.id]: { text: vip ? "Comped as VIP" : "VIP removed", ok: true } }));
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: adminKeys.registrations(retreatId) }),
        queryClient.invalidateQueries({ queryKey: adminKeys.sales() }),
        queryClient.invalidateQueries({ queryKey: adminKeys.retreats() }),
      ]);
    } catch (e) {
      setRowMsg((m) => ({
        ...m,
        [reg.id]: { text: e instanceof Error ? e.message : "Failed", ok: false },
      }));
    } finally {
      setBusyId(null);
    }
  }

  const filterTitle = retreatId ? list[0]?.retreatTitle ?? "this retreat" : null;

  async function runImport() {
    setImporting(true);
    setImportResult(null);
    setImportErr(null);

    try {
      let text = csvText;

      // If a file is selected, read it and prefer that over textarea content
      const file = fileRef.current?.files?.[0];
      if (file) {
        text = await file.text();
      }

      if (!text.trim()) {
        setImportErr("Paste CSV text or select a file.");
        return;
      }

      const result = await api.post<ImportResult>("/api/admin/registrations/import", {
        csv: text,
      });
      setImportResult(result);

      // Refresh list if anything was imported
      if (result.imported > 0) {
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: adminKeys.registrations(retreatId) }),
          queryClient.invalidateQueries({ queryKey: adminKeys.sales() }),
          queryClient.invalidateQueries({ queryKey: adminKeys.retreats() }),
        ]);
      }
    } catch (e) {
      setImportErr(e instanceof Error ? e.message : "Import failed");
    } finally {
      setImporting(false);
    }
  }

  const statusOptions = useMemo(
    () =>
      ["all", ...Array.from(new Set(list.map((row) => row.status))).sort()].map((value) => ({
        value,
        label: value === "all" ? "All statuses" : value,
      })),
    [list],
  );
  const filters = useMemo(
    () => [
      (row: Registration) => statusFilter === "all" || row.status === statusFilter,
      (row: Registration) =>
        balanceFilter === "all" ||
        (balanceFilter === "outstanding" && row.balanceDueCents > 0 && row.status !== "fully-paid") ||
        (balanceFilter === "paid" && (row.balanceDueCents <= 0 || row.status === "fully-paid")),
    ],
    [balanceFilter, statusFilter],
  );
  const {
    rows: tableRows,
    sort,
    requestSort,
  } = useAdminTable<Registration, RegistrationSortKey>({
    rows: list,
    search,
    initialSort: { key: "date", direction: "desc" },
    sorters: registrationSorters,
    searchText: registrationSearchText,
    filters,
  });

  const columns: Array<AdminDataTableColumn<Registration, RegistrationSortKey>> = [
    {
      id: "date",
      header: "Date",
      sortKey: "date",
      width: "8.5rem",
      nowrap: true,
      render: (r) => formatDate(r.createdAt),
    },
    {
      id: "retreat",
      header: "Retreat",
      sortKey: "retreat",
      width: "14rem",
      render: (r) => r.retreatTitle ?? <span style={{ opacity: 0.4 }}>-</span>,
    },
    {
      id: "email",
      header: "Email",
      sortKey: "email",
      width: "18rem",
      render: (r) => (
        <>
          <strong>{r.name}</strong>
          <span className="sub">{r.email}</span>
        </>
      ),
    },
    {
      id: "tier",
      header: "Tier",
      sortKey: "tier",
      width: "11rem",
      render: (r) => r.tierName ?? <span style={{ opacity: 0.4 }}>-</span>,
    },
    {
      id: "paid",
      header: "Paid",
      sortKey: "paid",
      width: "8rem",
      nowrap: true,
      render: (r) => formatMoney(r.amountPaidCents),
    },
    {
      id: "balance",
      header: "Balance",
      sortKey: "balance",
      width: "8rem",
      nowrap: true,
      render: (r) => formatMoney(r.balanceDueCents),
    },
    {
      id: "status",
      header: "Status",
      sortKey: "status",
      width: "10rem",
      render: (r) => <span className={`badge badge-${r.status}`}>{r.status}</span>,
    },
    {
      id: "vip",
      header: "VIP",
      width: "11rem",
      render: (r) => {
        const isVip = (r.addOns?.length ?? 0) > 0;
        const comped = isVip && r.addOns.every((a) => (a.priceCents ?? 0) === 0);
        const msg = rowMsg[r.id];
        if (r.status === "cancelled") return <span style={{ opacity: 0.4 }}>-</span>;
        return (
          <div className="bal-actions">
            {isVip ? (
              <>
                <span className="badge badge-vip">{comped ? "VIP · comped" : "VIP"}</span>
                {comped && (
                  <button
                    type="button"
                    className="btn-link-sm"
                    disabled={busyId === r.id}
                    onClick={() => toggleVip(r, false)}
                  >
                    Remove
                  </button>
                )}
              </>
            ) : (
              <button
                type="button"
                className="btn-link-sm"
                disabled={busyId === r.id}
                onClick={() => toggleVip(r, true)}
              >
                Make VIP (free)
              </button>
            )}
            {msg && <span className={msg.ok ? "bal-msg ok" : "bal-msg err"}>{msg.text}</span>}
          </div>
        );
      },
    },
    {
      id: "balance-link",
      header: "Balance link",
      width: "15rem",
      render: (r) => {
        const msg = rowMsg[r.id];
        return r.status === "balance-due" && r.balanceDueCents > 0 ? (
          <div className="bal-actions">
            <button
              type="button"
              className="btn-link-sm primary"
              disabled={busyId === r.id}
              onClick={() => sendBalanceLink(r, true)}
            >
              Email link
            </button>
            <button
              type="button"
              className="btn-link-sm"
              disabled={busyId === r.id}
              onClick={() => sendBalanceLink(r, false)}
            >
              Copy link
            </button>
            {msg && <span className={msg.ok ? "bal-msg ok" : "bal-msg err"}>{msg.text}</span>}
          </div>
        ) : (
          <span style={{ opacity: 0.4 }}>-</span>
        );
      },
    },
  ];

  return (
    <section>
      <style>{css}</style>
      <div className="hdr">
        <h1 className="aflt-admin-title">
          {retreatId ? `Purchases · ${filterTitle}` : "Registrations"}
        </h1>
        {retreatId && (
          <Link to="/admin/registrations" className="btn-ghost sm">
            View all retreats
          </Link>
        )}
      </div>

      {/* ---- import panel ---- */}
      <div className="paper-card import-card">
        <span className="form-legend">Import sales (CSV)</span>
        <p className="note" style={{ marginTop: 0, marginBottom: "0.85rem" }}>
          Backfills past registrations. Amounts in dollars (e.g.{" "}
          <code>450</code> or <code>$450.00</code>). Re-running is safe — duplicate rows are
          skipped. Expected columns:
        </p>
        <p className="mono-hint">{IMPORT_COLUMNS}</p>
        <p className="note" style={{ marginBottom: "0.85rem" }}>
          Required: <code>retreat_slug</code>, <code>email</code>, <code>amount_paid</code>.{" "}
          <code>tier_type</code> is one of <code>full</code> / <code>deposit</code> /{" "}
          <code>addon</code> (used to derive status when <code>status</code> is blank).
        </p>
        <label className="fld">
          <span>Paste CSV</span>
          <textarea
            value={csvText}
            onChange={(e) => setCsvText(e.target.value)}
            placeholder={IMPORT_COLUMNS}
            rows={6}
          />
        </label>
        <label className="fld">
          <span>…or upload a file</span>
          <input
            ref={fileRef}
            type="file"
            accept=".csv,text/csv"
            style={{ paddingLeft: 0, paddingRight: 0, border: "none", background: "none" }}
          />
        </label>
        {importErr && <p className="err">{importErr}</p>}
        {importResult && (
          <div className="import-result">
            <strong>{importResult.imported}</strong> imported ·{" "}
            <strong>{importResult.skipped}</strong> skipped ·{" "}
            <strong>{importResult.errors.length}</strong> errors
            {importResult.errors.length > 0 && (
              <ul className="import-errors">
                {importResult.errors.slice(0, 100).map((e, i) => (
                  <li key={i}>{e}</li>
                ))}
              </ul>
            )}
          </div>
        )}
        <div className="actions">
          <button type="button" className="btn-woven" onClick={runImport} disabled={importing}>
            {importing ? "Importing…" : "Import"}
          </button>
        </div>
      </div>

      {/* ---- list ---- */}
      <div style={{ marginTop: "2rem" }}>
        {registrationsQuery.isLoading ? (
          <p style={{ fontFamily: fonts.body, color: `${colors.navy}aa` }}>Loading…</p>
        ) : (
          <>
            <AdminTableControls
              search={search}
              onSearchChange={setSearch}
              placeholder="Search registrations, emails, retreats..."
              totalCount={list.length}
              resultCount={tableRows.length}
            >
              <AdminTableFilterSelect
                label="Status"
                value={statusFilter}
                onChange={setStatusFilter}
                options={statusOptions}
              />
              <AdminTableFilterSelect
                label="Balance"
                value={balanceFilter}
                onChange={setBalanceFilter}
                options={[
                  { value: "all", label: "All balances" },
                  { value: "outstanding", label: "Outstanding" },
                  { value: "paid", label: "Paid up" },
                ]}
              />
            </AdminTableControls>
            {list.length === 0 ? (
              <p style={{ fontFamily: fonts.body, color: `${colors.navy}aa` }}>No registrations yet.</p>
            ) : tableRows.length === 0 ? (
              <p style={{ fontFamily: fonts.body, color: `${colors.navy}aa` }}>No registrations match those filters.</p>
            ) : (
              <AdminDataTable
                columns={columns}
                rows={tableRows}
                getRowKey={(r) => r.id}
                sort={sort}
                onSort={requestSort}
                minWidth={1400}
              />
            )}
          </>
        )}
      </div>
    </section>
  );
}

const css = `
  .hdr { display: flex; align-items: center; justify-content: space-between; gap: 1rem; margin-bottom: 28px; }
  .btn-ghost {
    display: inline-block; text-decoration: none;
    background: ${colors.cream}; border: 1px solid rgba(201,147,76,0.55); color: ${colors.navy};
    font-family: ${fonts.body}; font-size: 0.7rem; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase;
    border-radius: 7px; padding: 9px 16px; cursor: pointer;
  }
  .btn-ghost:hover { box-shadow: 0 6px 14px -8px rgba(8,45,61,0.35); }
  .btn-ghost.sm { padding: 6px 12px; font-size: 0.62rem; }

  .btn-woven {
    display: inline-flex; align-items: center; gap: 7px; padding: 10px 20px;
    background: ${colors.berryRose}; color: #fff; font-family: ${fonts.body};
    font-size: 0.7rem; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase;
    border: none; border-radius: 7px; cursor: pointer;
    outline: 2px dashed rgba(255,255,255,0.45); outline-offset: -5px; box-shadow: 0 2px 8px rgba(212,96,106,0.30);
  }
  .btn-woven:hover { background: #c0535d; }
  .btn-woven:disabled { opacity: 0.6; cursor: default; }

  .admin-data-table .sub { display: block; font-size: 11px; color: rgba(8,45,61,0.47); }

  .paper-card { background: #fff; border: 1.5px dashed rgba(201,147,76,0.40); border-radius: 16px; padding: 1.5rem 1.75rem; }
  .import-card { margin-bottom: 0; }
  .form-legend { font-family: ${fonts.logo}; font-weight: 500; font-size: 1.2rem; color: ${colors.navy}; display: block; margin-bottom: 1.25rem; }
  .fld { display: block; margin-bottom: 1rem; }
  .fld > span { display: block; font-family: ${fonts.body}; font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.1em; color: rgba(8,45,61,0.65); margin-bottom: 0.35rem; }
  .fld textarea {
    width: 100%; min-height: 90px; padding: 0.7rem 0.9rem; border: 1px solid rgba(201,147,76,0.40); border-radius: 12px;
    background: ${colors.cream}; color: ${colors.navy}; font-family: ui-monospace,Menlo,monospace; font-size: 12px; outline: none; box-sizing: border-box;
  }
  .fld textarea:focus { border-color: ${colors.berryRose}; box-shadow: 0 4px 14px -8px rgba(212,96,106,0.55); }

  .actions { display: flex; gap: 0.6rem; margin-top: 0.5rem; }
  .err { color: ${colors.berryRose}; font-family: ${fonts.body}; font-weight: 600; font-size: 0.85rem; margin: 0.5rem 0; }
  .note { font-family: ${fonts.body}; font-size: 0.75rem; color: rgba(8,45,61,0.5); margin-top: 0.75rem; }
  .mono-hint { font-family: ui-monospace,Menlo,monospace; font-size: 11px; color: rgba(8,45,61,0.65); background: ${colors.blushLight}; border-radius: 8px; padding: 0.5rem 0.75rem; margin-bottom: 0.6rem; word-break: break-all; }

  .import-result { margin: 0.75rem 0; padding: 0.75rem 1rem; background: rgba(60,179,113,0.08); border: 1px solid rgba(60,179,113,0.25); border-radius: 10px; font-family: ${fonts.body}; font-size: 13.5px; color: rgba(8,45,61,0.85); }
  .import-errors { margin: 0.5rem 0 0; padding-left: 1.2rem; font-size: 12px; color: ${colors.berryRose}; }
  .import-errors li { margin-bottom: 0.2rem; }

  .bal-actions { display: flex; flex-wrap: wrap; align-items: center; gap: 0.4rem; }
  .btn-link-sm {
    background: ${colors.cream}; border: 1px solid rgba(201,147,76,0.55); color: ${colors.navy};
    font-family: ${fonts.body}; font-size: 0.6rem; font-weight: 700; letter-spacing: 0.05em; text-transform: uppercase;
    border-radius: 6px; padding: 5px 9px; cursor: pointer; white-space: nowrap;
  }
  .btn-link-sm:hover { box-shadow: 0 6px 14px -8px rgba(8,45,61,0.35); }
  .btn-link-sm.primary { background: ${colors.berryRose}; color: #fff; border-color: transparent; }
  .btn-link-sm.primary:hover { background: #c0535d; }
  .btn-link-sm:disabled { opacity: 0.5; cursor: default; }
  .bal-msg { display: block; flex-basis: 100%; font-family: ${fonts.body}; font-size: 11px; font-weight: 600; margin-top: 2px; word-break: break-all; }
  .bal-msg.ok { color: #2d7a52; }
  .bal-msg.err { color: ${colors.berryRose}; }

  .badge { display: inline-block; padding: 2px 9px; border-radius: 999px; font-size: 11px; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase; font-family: ${fonts.body}; }
  .badge-pending      { background: rgba(201,147,76,0.15); color: rgba(8,45,61,0.75); }
  .badge-confirmed    { background: rgba(100,149,237,0.15); color: #3155a0; }
  .badge-balance-due  { background: rgba(255,165,0,0.15); color: #8a5a00; }
  .badge-fully-paid   { background: rgba(60,179,113,0.15); color: #2d7a52; }
  .badge-cancelled    { background: rgba(212,96,106,0.15); color: ${colors.berryRose}; }
  .badge-vip          { background: rgba(201,147,76,0.20); color: #7a5416; }
`;
